import React, { FunctionComponent, useEffect, useState } from 'react';

import styles from './TwitterTweetCard.module.scss';
import CardTitle from '../CardTitle';
import NoData from '../NoData';
import { path } from 'app/utils/path';
import { _t } from 'app/utils/i18n';
import { useMutation } from 'react-query';
import { loadConversationReplies } from 'tweets/Requests';
import { useModal } from 'app/context/Modal';
import { Grid } from '@material-ui/core';
import { checkTranslated } from 'app/utils/helpers';
import { storeInfluencerName } from 'influencers/Requests';
import TweetCardContent from '../TweetCardContent';
import PostCardTopBar from '../PostCardTopBar';
import UserInformation from '../UserInformation';
import {
  useHandleNavigateInfluencerOrEntity,
  useOpenModalToAddUserAsInfluencer,
} from 'app/utils/check-influencer-user';
import PostCardTextContent from '../PostCardTextContent';
import TwitterTweetCardMedia from '../TwitterTweetCardMedia';
import DateLocation from '../DateLocation';
import TwitterPostCardStatus from '../TwitterPostCardStatus';
import {
  CommentTwitter,
  ExpandTwitter,
  LoveTwitter,
  RetweetTwitter,
} from 'app/assets/images/icons';
import TwitterTweetCardTopicIcons from '../TwitterTweetCardTopicIcons';

interface Props {
  data: any;
  compId?: string;
  title?: string;
  tweetRow?: boolean;
  isReport?: boolean;
  isBigSize?: boolean;
  topic_id?: number;
  tweetType?: 'negative' | 'positive' | 'neutral' | 'all';
  showConversationFlag?: boolean;
  isContentClickable?: boolean;
  is_sample?: boolean;
  showConversationComponent?: boolean;
  isTopicCard?: boolean;
  onItemClickAction?: (word: any, wordType: string) => void;
  detailsUrl?: string;
  quoteUrl?: string;
}

const TwitterTweetCard: FunctionComponent<Props> = ({
  data,
  compId,
  title,
  tweetRow,
  isReport,
  isBigSize,
  topic_id,
  tweetType,
  showConversationFlag,
  isContentClickable,
  is_sample,
  showConversationComponent,
  isTopicCard,
  onItemClickAction,
  detailsUrl,
  quoteUrl,
}) => {
  const {
    likes_count,
    replies_count,
    retweets_count,
    views,
    bookmarks_count,
    influencer,
  }: any = data;
  const [isTranslated, setIsTranslated] = useState(false);
  const [openModalToAddUserAsInfluencer] =
    useOpenModalToAddUserAsInfluencer(storeInfluencerName);
  const [handleNavigateInfluencerOrEntity] =
    useHandleNavigateInfluencerOrEntity();
  const [conversationRepliesList, setConversationRepliesList] = useState([]);
  const pathname = location?.pathname?.split('/')?.[1];
  const modal = useModal();
  const [showConversation, setShowConversation] = useState(isReport);
  const translationToggle = () => {
    if (isTranslated === true) {
      setIsTranslated(false);
    } else {
      setIsTranslated(true);
    }
  };
  const {
    mutateAsync: conversationReplies,
    isLoading: conversationRepliesLoad,
  } = useMutation(loadConversationReplies, {
    onSuccess: (data) => {
      setConversationRepliesList(data?.payload?.records);
      setShowConversation(true);
    },
  });
  useEffect(() => {
    if (showConversationComponent) {
      conversationReplies({ id: data?.id, is_sample });
    }
  }, []);
  const handleConvoFn = async () => {
    if (!!data?.show_conversation) {
      if (!conversationRepliesList?.length) {
        await conversationReplies?.({ id: data?.id });
      }
      if (showConversation && !!conversationRepliesList?.length) {
        setShowConversation(false);
      }
      if (!showConversation && !!conversationRepliesList?.length) {
        setShowConversation(true);
      }
    } else {
      modal.show({
        message: _t(`basic:reply_conversation`),
        onCancelText: _t('basic:ok'),
      });
    }
  };
  return (
    <div
      id={`${compId}-TwitterTweetCard-${data.id}`}
      className={styles.TwitterTweetCard}
    >
      {title && <CardTitle title={title} compId={compId!} />}
      <div
        className={`${styles.TwitterTweetCard} ${tweetRow ? styles.TweetRow : ''}`}
      >
        {!data.id ? (
          <NoData />
        ) : (
          <div className={styles.TwitterTweetCard_Container}>
            {/*top bar*/}
            {!isReport && (
              <PostCardTopBar
                translationToggle={translationToggle}
                showConversationFlag={showConversationFlag}
                compId={`${compId}-TwitterTweetCard-${data.id}`}
                conversationRepliesLoad={conversationRepliesLoad}
                handleConvoFn={handleConvoFn}
                showConversation={showConversation}
                platformUrl={data?.url}
                content={data?.content}
                contentTranslated={data?.content_translated}
                postId={data?.id}
                showQuoteIcon={
                  data?.tweet_type?.value?.toLowerCase() === 'quote' ||
                  data?.is_quote
                }
                showReplyIcon={
                  data?.tweet_type?.value?.toLowerCase() === 'reply' ||
                  data?.is_reply
                }
                postLanguages={data?.languages}
                showRetweetIcon={data?.is_retweet}
                platform={data?.platform}
              />
            )}
            {/*top bar*/}
            {/*convo list*/}
            {!!conversationRepliesList?.length &&
            showConversation &&
            showConversationFlag ? (
              <div className="p-20 mb-10">
                {conversationRepliesList?.map((item: any) => (
                  <TweetCardContent
                    key={item?.id}
                    conversationData={item}
                    conversationList={conversationRepliesList}
                    openModalToAddInfluencer={(url, name) =>
                      openModalToAddUserAsInfluencer(
                        name,
                        url,
                        _t('hashtags:open_in_twitter'),
                      )
                    }
                    handleInfluencer={handleNavigateInfluencerOrEntity}
                  />
                ))}
                <div
                  className="mt-10 w-full"
                  style={{
                    height: '1px',
                    background: 'var(--borderColor)',
                  }}
                />
              </div>
            ) : (
              <></>
            )}
            {/*convo list*/}
            {/*content*/}
            <div
              data-type={data?.url}
              className={`${styles.TwitterTweetCard_Container_content} post_card_url ${isBigSize ? 'p-line-20 ' : 'p-line-10'}`}
            >
              <Grid
                item
                className={`${styles.tweetSection} ${styles.sectionSeparator} ${
                  styles.usernameSection
                } ${topic_id && styles.topicTweets}`}
              >
                <Grid style={{ width: '100%' }} item>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1.5rem',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div>
                      <UserInformation
                        profileImage={data?.profile_image_url}
                        name={
                          checkTranslated(
                            data?.influencer?.name?.original,
                            data?.influencer?.name?.arabic,
                          ) || data?.username
                        }
                        isSmallerSize={
                          pathname === 'influencers' || pathname === 'entities'
                        }
                        postType={tweetType}
                        username={data?.username}
                        isVerified={data?.is_profile_verified}
                        handleInfluencerOrEntity={() =>
                          handleNavigateInfluencerOrEntity(data?.influencer)
                        }
                        isClickable={!isReport}
                        isUserAdded={influencer?.influencer_id !== 0}
                        openModalToAddInfluencer={() =>
                          openModalToAddUserAsInfluencer(
                            data?.username,
                            data?.influencer?.url,
                            _t('hashtags:open_in_twitter'),
                          )
                        }
                      />
                      <PostCardTextContent
                        content={data?.content}
                        storeInfluencerName={storeInfluencerName}
                        detailsUrl={`${detailsUrl ? detailsUrl : path.posts.TWITTER_POST_DETAILS_URI}/${data?.id}`}
                        isContentClickable={isContentClickable}
                        isReport={isReport}
                        isTranslated={isTranslated}
                        mentions={data?.tweet_mentions}
                        openText={_t('hashtags:open_in_twitter')}
                      />
                    </div>
                    {isTopicCard && topic_id && (
                      <TwitterTweetCardTopicIcons
                        hashtags_count={data?.hashtags_count}
                        topic_id={topic_id}
                        item_id={data?.id}
                        match_percentage={data?.match_percentage}
                        onItemClickAction={onItemClickAction}
                      />
                    )}
                  </div>

                  <TwitterTweetCardMedia
                    isTopicCard={isTopicCard}
                    attachments={
                      data?.attachments ||
                      data?.image_url?.map((img: string) => ({ url: img })) ||
                      []
                    }
                  />
                  {!!data?.has_parent &&
                    data?.parent_tweet &&
                    Number(data?.parent_tweet?.id) !== 0 && (
                      <TweetCardContent
                        parentTweetData={data?.parent_tweet}
                        openModalToAddInfluencer={(url, name) =>
                          openModalToAddUserAsInfluencer(
                            name,
                            url,
                            _t('hashtags:open_in_twitter'),
                          )
                        }
                        handleInfluencer={handleNavigateInfluencerOrEntity}
                        isQuote={
                          !!data?.has_parent &&
                          Number(data?.parent_tweet?.id) !== 0
                        }
                        isInstagram={data?.platform === _t('trends:instagram')}
                        quoteUrl={quoteUrl}
                      />
                    )}

                  <DateLocation
                    created_at={data?.created_at}
                    location={data?.location || data?.country?.name}
                    views={data?.views}
                  />
                  <TwitterPostCardStatus
                    tweetStatus={{
                      likes_count,
                      replies_count,
                      retweets_count,
                      bookmarks_count,
                      views,
                    }}
                    isReport={isReport}
                  />
                  {isReport && (
                    <div className={`${styles.TwitterIconsBar}`}>
                      <div>
                        <CommentTwitter />
                      </div>
                      <div>
                        <RetweetTwitter />
                      </div>

                      <div>
                        <LoveTwitter />
                      </div>
                      <div>
                        <ExpandTwitter />
                      </div>
                    </div>
                  )}
                </Grid>
              </Grid>
            </div>
            {/*content*/}
          </div>
        )}
      </div>
    </div>
  );
};

export default TwitterTweetCard;
